/**
 * MapOverlayButton
 *
 * A consistent button component for map overlay controls.
 * Supports icon-only and icon+text variants.
 */
import React from "react"
import { Pressable } from "react-native"
import { useStyle } from "react-native-style-utilities"

import { AppIcons } from "@/theme/icons/AppIcons"
import { renderIconFromIconProp } from "@/theme/icons/renderIconFromIconProp"
import { AppText } from "@/theme/Text"
import { testIds } from "@/theme/test-id"
import { getFontName } from "@/theme/theme/fonts"

import { MAP_OVERLAY_STYLES, useMapOverlayStyle } from "./styles"

import type { IconProp } from "@/theme/icons/AppIcons"
import type { TestId } from "@/theme/test-id"
import type { PressableProps, StyleProp, ViewStyle } from "react-native"

export interface MapOverlayButtonProps
  extends Omit<PressableProps, "children" | "style"> {
  /** Icon to display */
  IconComponent: IconProp
  /** Optional text label */
  text?: string
  /** Test ID for testing */
  id?: TestId
  /** Whether the button is disabled */
  isDisabled?: boolean
  /** Custom style override */
  style?: StyleProp<ViewStyle>
  /** Icon size override (default: 22) */
  iconSize?: number
}

const DEFAULT_ICON_SIZE = 22

export function MapOverlayButton({
  IconComponent,
  text,
  id,
  isDisabled = false,
  style,
  iconSize = DEFAULT_ICON_SIZE,
  onPress,
  ...rest
}: MapOverlayButtonProps): React.JSX.Element {
  const themeStyles = useMapOverlayStyle()

  // Resolve icon component
  const Icon =
    typeof IconComponent === "string" ? AppIcons[IconComponent] : IconComponent

  const hasText = typeof text === "string" && text.length > 0

  const baseStyle = useStyle(
    () => [
      MAP_OVERLAY_STYLES.buttonBase,
      hasText ? MAP_OVERLAY_STYLES.pillButton : MAP_OVERLAY_STYLES.iconButton,
      {
        backgroundColor: themeStyles.backgroundColor,
        borderColor: themeStyles.borderColor,
      },
      style,
    ],
    [hasText, themeStyles.backgroundColor, themeStyles.borderColor, style],
  )

  return (
    <Pressable
      accessible
      accessibilityRole="button"
      accessibilityState={{ disabled: isDisabled }}
      disabled={isDisabled}
      onPress={onPress}
      {...rest}
      {...testIds(id)}
      style={({ pressed }) => [
        baseStyle,
        pressed && {
          backgroundColor: themeStyles.pressedBackgroundColor,
          ...MAP_OVERLAY_STYLES.pressed,
        },
        isDisabled && { opacity: 0.5 },
      ]}
    >
      {renderIconFromIconProp(Icon, {
        size: iconSize,
        style: { color: themeStyles.iconColorActive },
      })}
      {hasText && (
        <AppText
          fontFamily={getFontName("Poppins_600SemiBold")}
          numberOfLines={1}
          style={useStyle(
            () => ({
              color: themeStyles.iconColorActive,
              fontSize: 13,
            }),
            [themeStyles.iconColorActive],
          )}
        >
          {text}
        </AppText>
      )}
    </Pressable>
  )
}
